/**
 * MomentsShareBar — QR + Wallet + Web Share + Copy-link snippet for the
 * owner dashboard page detail view.
 *
 * Native phone features:
 *   • navigator.share  → Android/iOS native share sheet
 *   • Google Wallet    → save the QR page URL as a wallet pass
 *   • Web NFC          → (Android Chrome only) write the URL to an NFC
 *                        sticker so tap-to-open replaces scanning.
 *
 * Owner rule #4: GLOBAL SNIPPET.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  QrCode, Copy, Share2, Download, Wallet, Radio, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ||
             (typeof window !== "undefined" ? window.location.origin : "");

export default function MomentsShareBar({
  slug,
  guestUrl,
  title    = "Moment",
  accentHex = "#f59e0b",
  testid   = "moments-share",
}) {
  const [busyNfc, setBusyNfc] = useState(false);
  const qrPng = `${API}/api/moments/m/${slug}/qr.png?size=1024`;
  const qrSvg = `${API}/api/moments/m/${slug}/qr.svg`;

  const _copy = async () => {
    try {
      await navigator.clipboard.writeText(guestUrl);
      toast.success("Guest link copied");
    } catch (_e) {
      toast.error("Copy failed — long-press to select");
    }
  };

  const _share = async () => {
    const shareData = {
      title: `Add your moments to ${title}`,
      text:  "Scan or tap to upload your photos & videos:",
      url:   guestUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await _copy();
      }
    } catch (_e) { /* user cancelled */ }
  };

  const _downloadPng = () => {
    const a = document.createElement("a");
    a.href = qrPng;
    a.download = `${slug}-qr.png`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const _downloadSvg = () => {
    const a = document.createElement("a");
    a.href = qrSvg;
    a.download = `${slug}-qr.svg`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const _writeNfc = async () => {
    if (typeof window === "undefined" || !("NDEFReader" in window)) {
      toast.error("Web NFC not supported on this device");
      return;
    }
    try {
      setBusyNfc(true);
      // eslint-disable-next-line no-undef
      const ndef = new NDEFReader();
      await ndef.write({ records: [{ recordType: "url", data: guestUrl }] });
      toast.success("NFC sticker written — tap to test");
    } catch (e) {
      toast.error(`NFC write failed: ${e.message || e}`);
    } finally {
      setBusyNfc(false);
    }
  };

  const _addToGoogleWallet = () => {
    // Best-effort: users can save the guestUrl as a bookmark. Full Google
    // Wallet Pass integration needs a service account signed JWT; we
    // hand off to the /moments/wallet route which will be added when the
    // user provides the Wallet Issuer creds.
    toast("Google Wallet pass coming soon", {
      description: "For now, use ‘Share’ or ‘Copy link’ — we'll add native Wallet passes as soon as your Wallet Issuer credentials are wired.",
    });
  };

  return (
    <div className="rounded-2xl border border-stone-200 dark:border-stone-800 p-4 space-y-4"
         data-testid={`${testid}-root`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-stone-800 dark:text-stone-100">
        <QrCode size={16} style={{ color: accentHex }}/>
        Guest access
      </div>

      <div className="flex items-start gap-4">
        <img
          src={qrPng}
          alt="Guest upload QR"
          className="w-28 h-28 rounded-xl ring-1 ring-stone-200 bg-white"
          data-testid={`${testid}-qr`}
        />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="rounded-lg bg-stone-100 dark:bg-stone-900 px-3 py-2 text-[13px] font-mono text-stone-700 dark:text-stone-300 truncate"
               data-testid={`${testid}-url`}>
            {guestUrl}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button" onClick={_share} data-testid={`${testid}-share-btn`}
              className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 bg-stone-900 text-white hover:bg-stone-700"
            >
              <Share2 size={13}/> Share
            </button>
            <button
              type="button" onClick={_copy} data-testid={`${testid}-copy-btn`}
              className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 bg-stone-200 hover:bg-stone-300"
            >
              <Copy size={13}/> Copy link
            </button>
            <button
              type="button" onClick={_downloadPng} data-testid={`${testid}-png-btn`}
              className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 bg-stone-200 hover:bg-stone-300"
            >
              <Download size={13}/> PNG
            </button>
            <button
              type="button" onClick={_downloadSvg} data-testid={`${testid}-svg-btn`}
              className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 bg-stone-200 hover:bg-stone-300"
            >
              <Download size={13}/> SVG (print)
            </button>
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-100 dark:border-stone-800">
            <button
              type="button" onClick={_addToGoogleWallet} data-testid={`${testid}-wallet-btn`}
              className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border border-stone-300 hover:border-stone-500"
            >
              <Wallet size={13}/> Add to Wallet
            </button>
            <button
              type="button" onClick={_writeNfc} disabled={busyNfc}
              data-testid={`${testid}-nfc-btn`}
              className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border border-stone-300 hover:border-stone-500 disabled:opacity-60"
            >
              {busyNfc ? <Loader2 className="animate-spin" size={13}/> : <Radio size={13}/>}
              Write NFC sticker
            </button>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-stone-500">
        Print the QR on table tents, invitations or signage. Guests scan
        with their phone camera — no app download.
      </p>
    </div>
  );
}
